﻿import {DateTime} from "@2d-front-monorepo/core";
import {
	ComponentWithChildrenParts,
	CreateVehicleModel,
	DriverId,
	DriverResult, EntityProfile,
	FullEntityRelation,
	GroupedPermit,
	IAllPermits,
	IAllReservations, IHolder,
	IntegraHolderData,
	IntegraZone, ParkingTariff,
	PermitResult,
	ReservationResult, ResidencyBehavior,
	ResidencyId,
	ResidencyResult,
	VehicleBehavior,
	VehicleId, VehiclePropertyFlags,
	VehicleResult
} from "@2d-front-monorepo/permits-business";

import {ComponentType, ReactNode} from "react";
import {permitsLogErrorToServer, recoverFromStaleChunk} from "../functions";
import {getGlobalContext} from "../hooks/getGlobalContext";
import DefaultArchivedPermitMenu from "./commons/permits/DefaultArchivedPermitMenu";
import DefaultPermitMenu from "./commons/permits/DefaultPermitMenu";
import DefaultReservationMenu from "./commons/reservations/DefaultReservationMenu";
import {default as CreateResidency} from "./commons/residencies/Create";
import {default as CreateVehicle} from "./commons/vehicles/Create";
import {EmptyComponent} from "./EmptyComponent";
import {TenantComponentMap} from "./types/TenantComponents";
import {TenantConfiguration} from "./types/TenantConfiguration";

const tenantConfigs: Record<string, () => Promise<{ default: TenantConfiguration }>> = {
	"pittsburgh": () => import("./pittsburgh/TenantConfig"),
	"coral-gables": () => import("./coral-gables/TenantConfig"),
	"coral-gables-monthly": () => import("./coral-gables-monthly/TenantConfig"),
	"gloucester": () => import("./gloucester/TenantConfig"),
	"gloucester-longbeach": () => import("./gloucester-longbeach/TenantConfig"),
	"melbourne": () => import("./melbourne/TenantConfig"),
	"sherbrooke": () => import("./sherbrooke/TenantConfig"),
	"gava": () => import("./gava/TenantConfig")
};

const loadedTenantMaps: Record<string, TenantConfiguration> = {};
const componentCache: Record<string, Record<string, ComponentType<any>>> = {};
const resolvedModules: Record<string, any> = {};

export interface CreateResidencyProps {
	holder: IHolder;
	holderData: IntegraHolderData;
	permits: IAllPermits;
	permit?: PermitResult;
	residency?: ResidencyResult;
	behavior?: ResidencyBehavior;
}

export interface CreateVehicleProps {
	holder: IHolder;
	permits: IAllPermits;
	permit?: PermitResult;
	vehicle?: VehicleResult;
	behavior?: VehicleBehavior;
	configuration?: EntityProfile<VehiclePropertyFlags>;
	createParts?: ComponentWithChildrenParts<CreateVehicleModel>;
	children?: ReactNode;
}

export interface PermitsTopMenuProps {
	permits: IAllPermits;
}

export interface ReservationsTopMenuProps {
	reservations: IAllReservations;
}

export interface DefaultPermitProps {
	now: DateTime;
	tariff: ParkingTariff;
	zone: IntegraZone;
	group: GroupedPermit;
	permit: PermitResult;
	permits: IAllPermits;
	driver: FullEntityRelation<DriverId, DriverResult> | undefined;
	residency: FullEntityRelation<ResidencyId, ResidencyResult> | undefined;
	vehicles: FullEntityRelation<VehicleId, VehicleResult>[];
}

export interface DefaultReservationProps {
	now: DateTime;
	tariff: ParkingTariff;
	reservation: ReservationResult;
	reservations: IAllReservations;
}

export interface PermitMenuProps extends DefaultPermitProps {
	editDriverEnabled?: boolean;
	editResidencyEnabled?: boolean;
	editVehicleEnabled?: boolean;
	cancelEnabled?: boolean;
	unparkEnabled?: boolean;
	/**
	 * Shows the enable/disable auto-renew entry. Defaults to false (hidden).
	 * Use "automatic" to show it based on the standard rules (valid permit status, not already renewed);
	 * a boolean forces it on/off (custom menus compute their own rule).
	 */
	autoRenewEnabled?: boolean | "automatic";
}

/** An archived permit card holds a bare {@link PermitResult}: no group, no related entities. */
export interface ArchivedPermitMenuProps {
	permit: PermitResult;
	renewEnabled?: boolean;
}

/**
 * The archived menu hands back its entries instead of rendering them, because it is allowed to be
 * EMPTY - and the card has to know that before it renders, so it can leave the popover out entirely
 * rather than show an empty menu button and its wrapper's flex gap.
 *
 * A tenant still both flips flags and appends entries:
 * `[...DefaultArchivedPermitMenu({...props, renewEnabled: true}), <Link key="x"/>]`.
 */
export type ArchivedPermitMenu = (props: ArchivedPermitMenuProps) => ReactNode[];

export interface PermitFooterProps extends DefaultPermitProps {
}

export interface PermitPageProps extends DefaultPermitProps {
}

export interface ReservationMenuProps extends DefaultReservationProps {
}

export interface ReservationFooterProps extends DefaultReservationProps {
}

export interface ReservationPageProps extends DefaultReservationProps {
}

export default class Registrations {
	private static _portal?: string;
	private static _preloadPromise?: Promise<void>;
	private static _slug?: string;

	private static get slug(): string {
		if (this._slug) return this._slug;

		const {installation} = getGlobalContext();
		this._portal = installation.portal;
		return this._slug = installation.slug;
	}

	public static getCreateResidency(): ComponentType<CreateResidencyProps> {
		return this.getPreloadedComponent(this.slug, "createResidency", CreateResidency);
	}

	public static getCreateVehicle(): ComponentType<CreateVehicleProps> {
		return this.getPreloadedComponent(this.slug, "createVehicle", CreateVehicle);
	}

	public static getArchivedPermitMenu(): ArchivedPermitMenu {
		return this.getPreloadedComponent(this.slug, "archivedPermitMenu", DefaultArchivedPermitMenu);
	}

	public static getPermitFooter(): ComponentType<PermitFooterProps> {
		return this.getPreloadedComponent(this.slug, "permitFooter");
	}

	public static getPermitMenu(): ComponentType<PermitMenuProps> {
		return this.getPreloadedComponent(this.slug, "permitMenu", DefaultPermitMenu);
	}

	public static getPermitPage(): ComponentType<PermitPageProps> {
		return this.getPreloadedComponent(this.slug, "permitPage");
	}

	public static getPermitsHome(): ComponentType<{}> {
		const slug = this.slug;
		const portal = this._portal;
		const tenantKey = portal ? `${slug}__${portal}` : slug;

		// Try portal-specific first, then fallback to slug
		try {
			const portalComponent = this.getPreloadedComponent(tenantKey, "permitHome");
			if (portalComponent !== EmptyComponent) {
				return portalComponent;
			}
		} catch {
		}

		return this.getPreloadedComponent(slug, "permitHome");
	}

	/** The tenant's configuration, already resolved by {@link preloadTenantComponents} (the tenant's
	 *  own home page comes from it, so it is loaded well before any wizard renders). */
	public static getTenantConfiguration(): TenantConfiguration | undefined {
		const slug = this.slug;
		const portal = this._portal;
		return (portal ? loadedTenantMaps[`${slug}__${portal}`] : undefined) ?? loadedTenantMaps[slug];
	}

	public static getPermitsTopMenu(): ComponentType<PermitsTopMenuProps> {
		return this.getPreloadedComponent(this.slug, "permitTopMenu");
	}

	public static getReservationFooter(): ComponentType<ReservationFooterProps> {
		return this.getPreloadedComponent(this.slug, "reservationFooter");
	}

	public static getReservationMenu(): ComponentType<ReservationMenuProps> {
		return this.getPreloadedComponent(this.slug, "reservationMenu", DefaultReservationMenu);
	}

	public static getReservationsTopMenu(): ComponentType<ReservationsTopMenuProps> {
		return this.getPreloadedComponent(this.slug, "reservationTopMenu");
	}

	public static async preloadTenantComponents(slug: string, portal?: string): Promise<void> {
		if (this._preloadPromise) return this._preloadPromise;

		const tenantKey = portal ? `${slug}__${portal}` : slug;

		this._preloadPromise = (async () => {
			// Load the tenant configuration first
			const tenantMap = (await this.loadTenantConfig(tenantKey) || await this.loadTenantConfig(slug)).components;
			if (tenantMap == null) {
				// A registered tenant with null components means its config chunk failed to
				// load — already logged in loadTenantConfig. Only an unregistered tenant is a
				// real config gap worth reporting.
				if (tenantConfigs[tenantKey] == null && tenantConfigs[slug] == null) {
					permitsLogErrorToServer(new Error(`Tenant [${portal ? `${tenantKey}|${slug}` : tenantKey}] does not have a components configuration`));
				}
				return;
			}

			// Preload all components and store the resolved modules
			const importPromises = Object.entries(tenantMap)
				.filter(([, importFn]) => importFn)
				.map(async ([componentKey, importFn]) => {
					try {
						const module = await importFn!();
						resolvedModules[`${slug}_${componentKey}`] = module;
						if (tenantKey !== slug) {
							resolvedModules[`${tenantKey}_${componentKey}`] = module;
						}
					} catch (error) {
						if (!recoverFromStaleChunk(error)) permitsLogErrorToServer(error);
					}
				});

			await Promise.allSettled(importPromises);
		})();

		return this._preloadPromise;
	}

	private static getPreloadedComponent<T extends ComponentType<any>>(slug: string, componentKey: keyof TenantComponentMap, fallback?: ComponentType<any>): T {
		if (!componentCache[slug]) {
			componentCache[slug] = {};
		}

		if (!componentCache[slug][componentKey]) {
			const moduleKey = `${slug}_${componentKey}`;
			const resolvedModule = resolvedModules[moduleKey];

			if (resolvedModule?.default) {
				componentCache[slug][componentKey] = resolvedModule.default;
			} else if (fallback) {
				componentCache[slug][componentKey] = fallback;
			} else {
				return EmptyComponent as unknown as T;
			}
		}

		return componentCache[slug][componentKey] as T;
	}

	private static async loadTenantConfig(slug: string): Promise<TenantConfiguration> {
		if (loadedTenantMaps[slug]) {
			return loadedTenantMaps[slug];
		}

		if (tenantConfigs[slug]) {
			try {
				const config = await tenantConfigs[slug]();
				loadedTenantMaps[slug] = config.default;
				return loadedTenantMaps[slug];
			} catch (error) {
				if (!recoverFromStaleChunk(error)) permitsLogErrorToServer(error);
			}
		}

		return {components: undefined!, portalSlug: "", slug: slug};
	}
}
